import { useRef, useState } from 'react';
import { Images } from 'lucide-react';
import { DjotContent } from '@/components/DjotContent';
import { DjotToolbar, handleDjotEditorKeyDown } from '@/components/DjotToolbar';
import { LiveEditor } from '@/components/LiveEditor';
import { ScrollableToolbarRow } from '@/components/ScrollableToolbarRow';
import { WikilinkSuggestionPopup } from '@/components/WikilinkSuggestionPopup';
import { EditorViewToggle, type EditorViewMode } from '@/components/EditorViewToggle';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useDjotEditor } from '@/hooks/useDjotEditor';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useWikilinkAutocomplete } from '@/hooks/useWikilinkAutocomplete';
import { cn } from '@/lib/utils';

interface DjotEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Minimum height of the editing surface. Defaults to 420px. */
  minHeight?: number;
  /** When true, the editor fills its flex parent instead of capping at `minHeight` — used by focus mode for full-page editing. */
  fullHeight?: boolean;
  /**
   * Raw/live/split layout control — optional; if not provided, the
   * editor uses its own view-mode state.
   */
  viewMode?: EditorViewMode;
  onViewModeChange?: (mode: EditorViewMode) => void;
  /**
   * When provided, replaces the toolbar's plain "Image" action with a
   * media-library picker. Kept as a prop rather than imported directly so
   * the editor stays decoupled from the media feature.
   */
  onRequestMedia?: () => void;
  /** Called on Ctrl/Cmd+Shift+F while editing — typically toggling focus mode. */
  onToggleFocus?: () => void;
  /** Called on Ctrl/Cmd+S while editing — typically saving a draft. */
  onSave?: () => void;
}

/**
 * A full Djot markup editing experience: a formatting toolbar (bold,
 * italic, headings, lists, links, tables, …), a plain raw-text editor,
 * and a `LiveEditor` surface that's directly editable and styled to look
 * like the rendered result as you type — both driven by the same
 * `applyEdit`/toolbar plumbing, so formatting actions work identically
 * no matter which surface has focus.
 *
 * The body offers three view modes via `EditorViewToggle`:
 *
 *  - **Raw edit** — just the plain-text editor, full width.
 *  - **Live edit** — just `LiveEditor`, full width: directly editable,
 *    styled to look like the rendered result as you type.
 *  - **Split editor** — the raw-text editor next to a true **read-only**
 *    rendered preview (`DjotContent`, the same renderer published
 *    articles use), NOT `LiveEditor`. Split mode is meant to answer "how
 *    will this look once published, side by side with what I'm typing,"
 *    which calls for the exact published-article rendering rather than
 *    the live-edit surface's approximate, editable styling — showing
 *    `LiveEditor` in both panes would just be two copies of the same
 *    thing.
 *
 * Narrow screens default to raw edit; wide screens default to split —
 * but the mode is always switchable regardless of viewport.
 */
export function DjotEditor({
  value,
  onChange,
  placeholder,
  className,
  minHeight = 420,
  fullHeight = false,
  viewMode: controlledViewMode,
  onViewModeChange,
  onRequestMedia,
  onToggleFocus,
  onSave,
}: DjotEditorProps) {
  const { bindActive, applyEdit } = useDjotEditor({ value, onChange });
  const isMobile = useIsMobile();
  // The toggle may be lifted to a surrounding page control — fall back
  // to the editor's own if it wasn't handed in.
  const [internalViewMode, setInternalViewMode] = useState<EditorViewMode>(() => (isMobile ? 'write' : 'split'));
  const viewMode = controlledViewMode ?? internalViewMode;
  const setViewMode = onViewModeChange ?? setInternalViewMode;

  const rawTextareaRef = useRef<HTMLTextAreaElement>(null);
  const wikilink = useWikilinkAutocomplete({ textareaRef: rawTextareaRef, value, onChange });

  const rawEditorEl = (
    <div className="relative h-full">
      <Textarea
        ref={(el) => {
          rawTextareaRef.current = el;
          bindActive(el);
        }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => bindActive(e.currentTarget)}
        onKeyDown={(e) => {
          // The suggestion dropdown gets first crack at navigation keys
          // (arrows/Enter/Tab/Escape) while it's open, so it doesn't fight
          // with the toolbar's own shortcuts or the textarea's defaults.
          if (wikilink.isOpen && wikilink.handleKeyDown(e)) return;
          handleDjotEditorKeyDown(e, applyEdit, {
            onToggleFocus,
            onSave,
          });
        }}
        onSelect={wikilink.onSelectionChange}
        onClick={wikilink.onSelectionChange}
        onKeyUp={wikilink.onSelectionChange}
        onScroll={wikilink.onSelectionChange}
        onBlur={wikilink.close}
        placeholder={placeholder}
        spellCheck
        className="h-full min-h-full resize-none rounded-none border-0 font-mono text-sm shadow-none focus-visible:ring-0"
      />
      {wikilink.isOpen && wikilink.position && (
        <WikilinkSuggestionPopup
          position={wikilink.position}
          suggestions={wikilink.suggestions}
          activeIndex={wikilink.activeIndex}
          isLoading={wikilink.isLoading}
          query={wikilink.query}
          onHover={wikilink.setActiveIndex}
          onSelect={wikilink.select}
        />
      )}
    </div>
  );

  const liveEditorEl = (
    <LiveEditor
      value={value}
      onChange={onChange}
      format="djot"
      placeholder={placeholder}
      registerTextarea={bindActive}
      onFormatKeyDown={(e) =>
        handleDjotEditorKeyDown(e, applyEdit, {
          onToggleFocus,
          onSave,
        })
      }
    />
  );

  // Split mode's right pane is a true read-only preview — the same
  // renderer published articles use — not the editable LiveEditor. See
  // the component doc comment above.
  const readOnlyPreviewEl = (
    <div className="h-full overflow-y-auto p-4">
      {value.trim() ? (
        <DjotContent content={value} />
      ) : (
        <p className="text-sm text-muted-foreground">Nothing to preview yet — start writing.</p>
      )}
    </div>
  );

  return (
    <div className={cn('space-y-0', fullHeight && 'flex min-h-0 h-full flex-col', className)}>
      {/* Toolbar is a single scrollable row: flex-wrap would make it
          taller (multiline) instead of scrollable, misplacing the "one
          row of actions" UI model and crowding the document. The
          raw/live/split toggle is intentionally not rendered here — the
          surrounding page owns it and positions it next to the format
          selector. `onRequestMedia` goes through the same scrollable
          row so it doesn't fight the toolbar for space. */}
      <ScrollableToolbarRow className="rounded-t-md border border-b-0 border-input bg-muted/40 p-1">
        {/* Insert media leads the row rather than trailing it — it's one
            of the most-used actions, and a far-left position is easier to
            reach than making the user hunt past every formatting control. */}
        {onRequestMedia && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 shrink-0"
              onClick={onRequestMedia}
              aria-label="Insert media"
            >
              <Images className="size-4" />
            </Button>
            <Separator orientation="vertical" className="mx-1 h-5" />
          </>
        )}
        <DjotToolbar applyEdit={applyEdit} />
      </ScrollableToolbarRow>

      {viewMode === 'write' && (
        <div
          className={cn('rounded-b-md border border-input', fullHeight && 'min-h-0 flex-1')}
          style={fullHeight ? undefined : { height: minHeight }}
        >
          {rawEditorEl}
        </div>
      )}

      {viewMode === 'live' && (
        <div
          className={cn('rounded-b-md border border-input', fullHeight && 'min-h-0 flex-1')}
          style={fullHeight ? undefined : { height: minHeight }}
        >
          {liveEditorEl}
        </div>
      )}

      {viewMode === 'split' && (
        <ResizablePanelGroup
          direction="horizontal"
          className={cn('rounded-b-md border border-input', fullHeight && 'min-h-0 flex-1')}
          style={fullHeight ? undefined : { height: minHeight }}
        >
          <ResizablePanel defaultSize={50} minSize={25}>
            {rawEditorEl}
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={50} minSize={25}>
            {readOnlyPreviewEl}
          </ResizablePanel>
        </ResizablePanelGroup>
      )}
    </div>
  );
}
